import React, { useCallback, useEffect, useState } from "react";
import { Sparkles, MessageSquare, Loader2 } from "lucide-react";
import { getReadiness, setSwitch, writeSummary } from "../../lib/uploadsApi";
import { useRole } from "../../lib/role.jsx";
import { useCopilot } from "../../lib/copilot.jsx";
import { cn } from "../../lib/utils";
import { Chip, Side } from "../uploads/FileUploadSource";
import { LayerHeader, LayerSwitch, LAYERS, NAME, TableScores } from "./ReadinessLayers";
import ReadinessCompare from "./ReadinessCompare";
import UnlockPanel from "./UnlockPanel";
import GoldPrompt from "./GoldPrompt";

const ST = { ready: ["Ready", "success"], limited: ["Usable with limits", "warning"], not_ready: ["Not ready", "critical"] };
const CELL = { good: "bg-success/70", weak: "bg-warning/70", missing: "bg-critical/70", na: "border border-dashed border-muted/50" };
const btn = "inline-flex items-center gap-1.5 h-9 px-3.5 rounded-sm text-[12.5px] font-medium disabled:opacity-45";

function Summary({ scope, cached }) {
  const [s, setS] = useState(cached || null);
  const [state, setState] = useState({ busy: false, error: null });
  const { askCopilot } = useCopilot();
  const go = () => { setState({ busy: true, error: null }); writeSummary(scope).then(x => { setS(x); setState({ busy: false, error: null }); }).catch(e => setState({ busy: false, error: e.message })); };
  return (
    <div className="tile p-4 text-[13px]" data-testid={`up-summary-${scope}`}>
      <div className="kpi-label flex items-center gap-1.5 mb-2"><Sparkles size={11} className="text-primary" />AI summary</div>
      {s ? <><div className="space-y-2 leading-relaxed" data-testid={`up-summary-text-${scope}`}>{s.text.split(/\n\s*\n/).map((p, i) => <p key={i}>{p}</p>)}</div>
        <div className="text-[11px] text-muted mt-2 font-mono-tabular">Written {new Date(s.generated_at).toLocaleString("en-PH")} · rewritten only when the data or results change</div></>
        : <button data-testid={`up-summary-write-${scope}`} disabled={state.busy} onClick={go} className={cn(btn, "border border-primary/50 text-primary")}>{state.busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}{state.busy ? "Writing…" : "Write summary"}</button>}
      {state.error && <div className="text-critical text-[12px] mt-1">{state.error}</div>}
      {s && <button data-testid={`up-summary-copilot-${scope}`} onClick={() => askCopilot("Explain the uploaded data quality and model readiness, and what to fix first.", { label: "Uploaded data readiness", origin: "actual", lines: [s.text.slice(0, 900)] })}
        className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-primary hover:underline"><MessageSquare size={12} />Ask Copilot</button>}
    </div>
  );
}

function Matrix({ r, onModel }) {
  return (
    <div className="tile overflow-hidden" data-testid="up-matrix">
      <div className="hidden md:block overflow-x-auto"><table className="w-full text-[12px]">
        <thead><tr className="text-[10px] uppercase tracking-widest text-muted"><th className="text-left px-3 py-2 border-b border-border">Model</th>
          {r.components.map(c => <th key={c.id} className="px-2 py-2 border-b border-border">{c.label}</th>)}<th className="px-3 py-2 border-b border-border text-left">Status</th></tr></thead>
        <tbody>{r.models.map(m => (
          <tr key={m.id} data-testid={`up-model-${m.id}`} onClick={() => onModel(m)} className="border-b border-border/60 cursor-pointer hover:bg-surface-2">
            <td className="px-3 py-2"><div className="font-medium">{m.label}</div><div className="text-[10px] text-muted">{m.group}{m.use_uploaded ? " · uses uploaded data" : ""}</div></td>
            {r.components.map(c => <td key={c.id} className="px-2 py-2"><div className={cn("h-4 rounded-sm mx-auto w-10", CELL[m.cells[c.id]])} title={m.cells[c.id]} /></td>)}
            <td className="px-3 py-2"><Chip tone={ST[m.status][1]}>{ST[m.status][0]}</Chip></td></tr>))}</tbody></table></div>
      <div className="md:hidden divide-y divide-border">{r.models.map(m => (
        <button key={m.id} data-testid={`up-model-card-${m.id}`} onClick={() => onModel(m)} className="w-full text-left px-3 py-2.5 flex items-center gap-2">
          <span className="flex-1 text-[13px]">{m.label}</span><Chip tone={ST[m.status][1]}>{ST[m.status][0]}</Chip></button>))}</div>
    </div>
  );
}

function ModelPanel({ m, layer, canEdit, summary, onClose, onChanged }) {
  const [state, setState] = useState({ busy: false, error: null });
  const sw = layer === "silver" && (m.switch === "engine" || m.switch === "forecast");
  const toggle = async () => { setState({ busy: true, error: null }); try { await setSwitch(m.id, !m.use_uploaded); await onChanged(); setState({ busy: false, error: null }); } catch (e) { setState({ busy: false, error: e.message }); } };
  const List = ({ title, items, tid }) => items.length ? <div data-testid={tid}><div className="kpi-label mb-1">{title}</div><ul className="list-disc pl-4 space-y-1 text-[12.5px]">{items.map(x => <li key={x}>{x}</li>)}</ul></div> : null;
  return (
    <Side onClose={onClose} testid="up-model-panel">
      <div className="p-5 space-y-4 text-sm">
        <div className="pr-10"><div className="text-[10px] uppercase tracking-[0.18em] text-primary font-semibold">{m.group} · {NAME[layer]}</div><div className="font-display text-lg font-semibold">{m.label}</div>
          <div className="mt-1"><Chip tone={ST[m.status][1]} testid="up-model-status">{ST[m.status][0]}</Chip></div></div>
        <List title="Missing components" items={m.missing} tid="up-model-missing" />
        <List title="Weak components" items={m.weak} tid="up-model-weak" />
        <List title="What to do next" items={m.steps} tid="up-model-steps" />
        <div className="tile p-3 space-y-2">
          <div className="kpi-label">Use uploaded data</div>
          {sw ? <>
            <button data-testid="up-model-switch" disabled={!canEdit || state.busy || (!m.use_uploaded && m.status === "not_ready")} onClick={toggle}
                    className={cn(btn, m.use_uploaded ? "bg-success text-white" : "border border-border")}>{state.busy && <Loader2 size={13} className="animate-spin" />}{m.use_uploaded ? "On — runs on uploaded data" : "Off — runs on sample data"}</button>
            <div className="text-[11.5px] text-muted">{m.status === "not_ready" ? "Can be switched on once the model is Ready or Usable with limits." : "Switching starts a rerun straight away; its screens show the Actual label."}</div></>
            : <div className="text-[12px] text-muted" data-testid="up-model-switch-note">{layer !== "silver" ? `This is the ${NAME[layer]} view — the switches use Silver data. Open the Silver view to switch this model.` : m.switch === "phase2" ? "Procurement reads this table from Phase 2 — there is no switch for it yet." : m.switch?.startsWith("follows:") ? `Follows the ${m.switch.split(":")[1].replace(/_/g, " ")} switch.` : "The classification reads actual data through the Actual Data Switch."}</div>}
          {state.error && <div className="text-critical text-[12px]">{state.error}</div>}
          {!canEdit && layer === "silver" && <div className="text-[11.5px] text-muted">View only — the IT Head switches models.</div>}
        </div>
        {layer === "silver" && <Summary scope="matrix" cached={summary} />}
      </div>
    </Side>
  );
}

const Legend = () => (
  <div className="flex flex-wrap gap-3 text-[11px] text-muted mt-2">{[["good", "Present and good"], ["weak", "Present but weak"], ["missing", "Missing"], ["na", "Not needed"]].map(([k, l]) => <span key={k} className="inline-flex items-center gap-1"><span className={cn("w-3 h-3 rounded-sm", CELL[k])} />{l}</span>)}</div>
);

// Console → Model Readiness per layer: Bronze (files as uploaded), Silver (every promoted table — the switches use it), Gold (certified only) and a comparison.
export default function ReadinessTab() {
  const { role } = useRole();
  const canEdit = role?.id === "it_head";
  const [layer, setLayer] = useState("silver");
  const [data, setData] = useState({});
  const [sel, setSel] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback((l, retry) => getReadiness(l, retry).then(x => { setData(d => ({ ...d, [l]: x })); setErr(null); }).catch(e => setErr(e.message)), []);
  const want = layer === "compare" ? LAYERS : [layer];
  const wantsBronze = want.includes("bronze");
  useEffect(() => { setSel(null); (layer === "compare" ? LAYERS : [layer]).forEach(l => load(l)); }, [layer, load]);
  const bz = data.bronze;
  useEffect(() => {
    if (!wantsBronze || bz?.state !== "running") return undefined;
    const t = setTimeout(() => load("bronze"), 2500);
    return () => clearTimeout(t);
  }, [bz, wantsBronze, load]);
  const retry = () => load("bronze", true);
  const reloadSilver = useCallback(() => load("silver"), [load]);
  const r = data[layer];
  const m = sel && r?.models?.find(x => x.id === sel);
  return (
    <div className="space-y-5" data-testid="tab-readiness">
      <LayerSwitch value={layer} onChange={setLayer} />
      {err && <div className="text-critical text-[12.5px]" data-testid="rd-error">{err}</div>}
      {layer === "compare" ? <ReadinessCompare data={data} onRetry={retry} /> : <>
        <LayerHeader layer={layer} r={r} onRetry={retry} />
        {!r ? <div className="tile p-6 text-sm text-muted" data-testid="up-loading">Loading model readiness…</div> : r.models ? <>
          <TableScores layer={layer} r={r} />
          {layer === "gold" && <GoldPrompt onChanged={() => { load("gold"); load("silver"); }} />}
          <div><Matrix r={r} onModel={(x) => setSel(x.id)} /><Legend /></div>
          {layer === "silver" && <UnlockPanel onChanged={reloadSilver} />}
          {layer === "silver" && <Summary scope="matrix" cached={r.summary} />}
        </> : null}
      </>}
      {m && <ModelPanel m={m} layer={layer} canEdit={canEdit} summary={r.summary} onClose={() => setSel(null)} onChanged={() => load("silver")} />}
    </div>
  );
}
